// Eltafouk: shared helpers for the close-disposition flow.

export const GREETING_ONLY_REASON = 'ترحيب فقط';

// «محتاجة متابعة» is the one outcome that must NOT resolve the conversation.
// Resolving it would be the exact failure we spent a day removing: the agent says
// "I need to come back to this" and the conversation leaves their list, with only a
// customer reply able to bring it back. It snoozes instead — it returns on its own
// tomorrow morning, or the moment the customer writes, whichever comes first.
// Exported so the modal, the header resolve button and the conversation-list resolve
// all branch on the same string.
export const FOLLOW_UP_OUTCOME = 'محتاجة متابعة';

export const isFollowUpOutcome = outcome => outcome === FOLLOW_UP_OUTCOME;

// A message page holds 20 rows, so a loaded thread of 20+ may have unseen older
// pages — only strictly-smaller threads are provably complete; otherwise we ask.
const FULL_THREAD_LIMIT = 20;

const INCOMING = 0;
const TEMPLATE = 3;

// Meta's own click-to-ad prefill, sent verbatim when the customer taps an ad without
// typing — no intent. An EDITED prefill («بكام؟») is a real question and keeps its
// questions. Mirrors GENERIC_AD_PREFILLS in the Ruby job.
const GENERIC_AD_PREFILLS = [
  'مرحبًا! هل يمكنني الحصول على مزيد من المعلومات حول هذا؟',
  'Hello! Can I get more info on this?',
];

const stripPunctuation = text =>
  text
    .replace(/[\p{P}\p{S}]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();

const hasNoIntent = text => {
  const stripped = stripPunctuation(text);
  if (!stripped) return true;
  return GENERIC_AD_PREFILLS.some(p => stripPunctuation(p) === stripped);
};

// The customer never said what they wanted: every incoming message is a tap on a
// button we offered (each offering bot message stamps its buttons on itself as
// content_attributes.greeting_buttons) or Meta's generic ad prefill. Media counts as
// real content. An agent reply does NOT disqualify — the team probes «أقدر أساعد
// حضرتك؟» on these and closes when nobody answers; what the agent said is irrelevant.
// Mirrors Conversations::ResolveGreetingOnlyConversationsJob.
export const isGreetingOnlyConversation = chat => {
  const messages = chat?.messages || [];
  if (!messages.length || messages.length >= FULL_THREAD_LIMIT) return false;

  const incoming = messages.filter(
    m => m.message_type === INCOMING && !m.private
  );
  if (!incoming.length) return false;

  // A list-stub conversation carries only its last message; the greeting template's
  // presence is the signal that we are looking at the real (tiny) thread.
  if (!messages.some(m => m.message_type === TEMPLATE)) return false;

  const offered = new Set();
  messages.forEach(m => {
    // Canned-response buttons (stamped with canned_button) are agent-offered:
    // tapping one is real intent, never a no-intent greeting tap. Mirrors the
    // same exclusion in the backend job.
    if (m.content_attributes?.canned_button) return;
    (m.content_attributes?.greeting_buttons || []).forEach(button =>
      offered.add(String(button).trim())
    );
  });

  return incoming.every(m => {
    if (m.attachments?.length) return false;
    const text = (m.content || '').trim();
    // Blank content with no attachment can be a REAL dropped-payload message
    // (known FB share/sticker fallback bugs) — conservative: not a tap.
    if (!text) return false;
    if (hasNoIntent(text)) return true;
    return offered.has(text);
  });
};

// Ask the once-per-customer classification question only while the contact card has
// no classification. The ERP→Chatwoot mirror fills it for known customers; the
// backend never overwrites an existing value, so asking on a stale/slim record is
// harmless.
export const needsContactClassification = chat => {
  const sender = chat?.meta?.sender;
  if (!sender) return false;
  return !sender.additional_attributes?.custom_customer_classification;
};
